import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import IcRoundArrowOutward from '~icons/ic/round-arrow-outward'
import {
  ControlSectionMessage,
  ControlSectionMessageType,
} from 'src/components/GenerationTask/MultiColumnGenerationForm/controls/ControlSection'
import { CreditsIcon } from 'src/components/Membership/MembershipIcon'
import { UIButton } from 'src/components/ui/Button'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import { useCheckEmailVerified } from 'src/features/User/ForceVerifyEmail/useForceVerifyEmail'
import { useSocialMedia } from 'src/hooks/socialMedia'
import { useMyInfo } from 'src/services/apollo/auth'
import { useFollowSocialMediaMutation } from 'src/services/apollo/quest'
import { getLocale } from 'src/stores/locale'
import { cx } from 'src/utils/cx'

interface SocialMediaFollowModalProps {
  isOpen: boolean
  onClose: () => void
  completedPlatforms?: string[]
  onSuccess?: () => Promise<void>
  /** v2 callback — if provided, used instead of GraphQL mutation */
  onFollowPlatform?: (platform: string) => Promise<void>
}

export function SocialMediaFollowModal({
  isOpen,
  onClose,
  completedPlatforms = [],
  onSuccess,
  onFollowPlatform,
}: SocialMediaFollowModalProps) {
  const { t } = useTranslation(['user'])
  const [loading, setLoading] = useState<string | null>(null)
  const { data: userInfo } = useMyInfo()
  const socialMedias = useSocialMedia({
    fixedItem: completedPlatforms,
  })
  const [followSocialMedia] = useFollowSocialMediaMutation()
  const checkEmailVerified = useCheckEmailVerified()

  const handlePlatformClick = async (
    platformKey: string,
    platformUrl: string,
    isCompleted: boolean,
  ) => {
    if (!(await checkEmailVerified())) return

    window.open(platformUrl, '_blank')

    if (isCompleted) return

    if (!loading) {
      setLoading(platformKey)

      try {
        if (onFollowPlatform) {
          await onFollowPlatform(platformKey)
          await onSuccess?.()
        } else {
          const result = await followSocialMedia({
            variables: { platform: platformKey },
          })
          if (result.data?.followSocialMedia?.success) {
            await onSuccess?.()
          }
        }
      } catch (error) {
        console.error('Error claiming social media reward:', error)
      } finally {
        setLoading(null)
      }
    }
  }

  return (
    <UIDialog
      open={isOpen}
      onClose={onClose}
      className="w-[30rem] max-w-[calc(100vw-2rem)]"
    >
      <UIDialogHeader heading={t('user:quest.social_follow.title')} />

      <UIDialogContent className="flex flex-col gap-4">
        <p className="text-sm text-zinc-600 dark:text-zinc-400">
          {t('user:quest.social_follow.description')}
        </p>

        <div className="space-y-3">
          {socialMedias.map(socialMedia => {
            const platformKey = socialMedia.key
            const isCompleted = completedPlatforms.includes(platformKey)
            const isLoading = loading === platformKey
            const IconComponent = socialMedia.icon

            const onClick = () => {
              if (!socialMedia.link) return
              handlePlatformClick(platformKey, socialMedia.link, isCompleted)
            }

            return (
              <button
                key={platformKey}
                type="button"
                disabled={isLoading}
                onClick={onClick}
                className={cx(
                  'w-full text-left p-4 border rounded-lg transition-colors flex items-center justify-between relative overflow-hidden',
                  'border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-700/50',
                )}
              >
                <div className="flex items-center gap-4">
                  <div className="w-9 h-9 flex items-center justify-center">
                    <IconComponent className="w-7 h-7" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-base">
                      {socialMedia.name}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <CreditsIcon className="size-4 text-purple-500" />
                    <span className="text-xl font-semibold tracking-tight text-purple-500">
                      {(10000).toLocaleString(getLocale())}
                    </span>
                  </div>
                  <IcRoundArrowOutward className="size-5 text-zinc-400 dark:text-zinc-500" />
                </div>

                {isCompleted && (
                  <div className="absolute top-0 right-0 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-[10px] font-bold px-2 py-0.5 rounded-bl-lg border-b border-l border-green-200 dark:border-green-800">
                    {t('user:quest.social_follow.claimed')}
                  </div>
                )}
              </button>
            )
          })}
        </div>

        {!userInfo?.emailVerified && (
          <ControlSectionMessage type={ControlSectionMessageType.Warning}>
            {t('user:quest.social_follow.email_verification_required')}
          </ControlSectionMessage>
        )}
      </UIDialogContent>

      <UIDialogFooter>
        <UIButton variant="outlined" onPress={onClose}>
          {t('common:close')}
        </UIButton>
      </UIDialogFooter>
    </UIDialog>
  )
}
